import { useState } from "react";
import { HiChevronDown, HiChevronRight } from "react-icons/hi2";
import { Box } from "../../../../components/Box";
import { Flex } from "../../../../components/Flex";
import { ExpansionPanel } from "../../../../components/ExpansionPanel";
import { Toggle } from "../../../../components/Toggle";
import { Paragraph } from "../../../../components/Paragraph";
import { CardLine } from "../../../IndividualView";
import {
  formatAnswerValue,
  formatFormsTimestamp,
} from "../../../../utils/formatters";
import {
  ProfileForm,
  ProfileFormAnswer,
  ProfileFormQuestion,
  FieldType,
} from "../../../../types/Forms";
import { answersChronological } from "./formsHelpers";
import {
  AnswerRow,
  AnswerTable,
  AnswerValue,
  ChangedTag,
  DisclosureButton,
  DocumentLink,
  FieldValue,
  FormTitle,
  HistoryRow,
  MetaLine,
  SectionCard,
  SectionTitle,
} from "../../../../components/FormAnswers";

interface AnswerRowData {
  answer: ProfileFormAnswer;
  value: string;
  changed: boolean;
}

/** Chronological rows for a question, each flagged if it changed on the previous. */
const buildRows = (question: ProfileFormQuestion): AnswerRowData[] => {
  const ordered = answersChronological(question.answers);
  return ordered.map((answer, index) => {
    const value = formatAnswerValue(answer, question.field_type);
    const previous =
      index > 0
        ? formatAnswerValue(ordered[index - 1], question.field_type)
        : null;
    return { answer, value, changed: index > 0 && value !== previous };
  });
};

const renderAnswerNode = (
  answer: ProfileFormAnswer,
  fieldType: FieldType | null
) => {
  const value = formatAnswerValue(answer, fieldType);
  if (answer.document) {
    return (
      <DocumentLink href={answer.document}>
        {value || "View document"}
      </DocumentLink>
    );
  }
  return value ? <AnswerValue value={value} fieldType={fieldType} /> : "(no answer)";
};

const metaText = (parts: Array<string | null | undefined>) =>
  parts.filter(Boolean).join("  ·  ");

/**
 * One question row: the latest answer fills the answer cell, older answers fold
 * under a toggle inside the same cell so the table keeps its alignment.
 */
const QuestionRow = ({
  question,
  rows,
}: {
  question: ProfileFormQuestion;
  rows: AnswerRowData[];
}) => {
  const [open, setOpen] = useState(false);

  const latest = rows.length ? rows[rows.length - 1] : null;
  const earlier = rows.slice(0, -1).reverse(); // newest-first among the older answers

  return (
    <AnswerRow title={question.title} fieldType={question.field_type}>
      {latest ? (
        <>
          <Flex alignItems="baseline" gap="10" flexWrap="wrap">
            <FieldValue>
              {renderAnswerNode(latest.answer, question.field_type)}
            </FieldValue>
            {latest.changed && <ChangedTag>changed</ChangedTag>}
          </Flex>
          <MetaLine>
            {metaText([
              latest.answer.individual?.reference,
              formatFormsTimestamp(latest.answer.created_on),
            ])}
          </MetaLine>

          {earlier.length > 0 && (
            <Box>
              <DisclosureButton
                type="button"
                aria-expanded={open}
                onClick={() => setOpen((prev) => !prev)}
              >
                {open ? (
                  <HiChevronDown size={14} />
                ) : (
                  <HiChevronRight size={14} />
                )}
                {earlier.length} earlier{" "}
                {earlier.length === 1 ? "answer" : "answers"}
              </DisclosureButton>

              {open &&
                earlier.map((row, index) => (
                  <HistoryRow key={`${question.id}-${index}`}>
                    <Flex alignItems="baseline" gap="10" flexWrap="wrap">
                      <FieldValue muted={!row.value}>
                        {renderAnswerNode(row.answer, question.field_type)}
                      </FieldValue>
                      {row.changed && <ChangedTag>changed</ChangedTag>}
                    </Flex>
                    <MetaLine>
                      {metaText([
                        row.answer.individual?.reference,
                        formatFormsTimestamp(row.answer.created_on),
                      ])}
                    </MetaLine>
                  </HistoryRow>
                ))}
            </Box>
          )}
        </>
      ) : (
        <FieldValue muted>Not answered</FieldValue>
      )}
    </AnswerRow>
  );
};

/**
 * By question: every question with its answers across all records, in the same
 * table the Applications Forms tab uses. The latest answer shows first, older
 * ones fold away under a toggle, and a "changed" tag marks where an answer
 * moved from the previous submission. "Changes only" hides questions whose
 * answer never changed. Duplicate answers are shown, not collapsed (flagged as
 * a potential backend data issue, see the proposal doc).
 */
export const FormsByQuestion = ({ forms }: { forms: ProfileForm[] }) => {
  const [changesOnly, setChangesOnly] = useState(false);

  const visibleForms = forms
    .map((form) => ({
      form,
      sections: (form.sections ?? [])
        .map((section) => {
          const questions = (section.questions ?? [])
            .map((question) => ({ question, rows: buildRows(question) }))
            .filter(({ rows }) =>
              changesOnly ? rows.some((row) => row.changed) : true
            );
          return { section, questions };
        })
        .filter(({ questions }) => questions.length > 0),
    }))
    .filter(({ sections }) => sections.length > 0);

  return (
    <Box>
      <Flex justifyContent="flex-end" alignItems="center" gap="8" mb={3}>
        <Paragraph fontSize={1} color="gray.60" my={0}>
          Changes only
        </Paragraph>
        <Toggle
          isChecked={changesOnly}
          onChange={() => setChangesOnly((prev) => !prev)}
        />
      </Flex>

      {visibleForms.length ? (
        visibleForms.map(({ form, sections }) => (
          <Box key={form.id} mb={3}>
            <ExpansionPanel
              title={<FormTitle>{form.name}</FormTitle>}
              defaultExpanded
            >
              {sections.map(({ section, questions }, sIdx) => (
                <SectionCard key={section.id ?? sIdx}>
                  <SectionTitle>
                    {section.title || `Section ${sIdx + 1}`}
                  </SectionTitle>
                  <AnswerTable>
                    {questions.map(({ question, rows }) => (
                      <QuestionRow
                        key={question.id}
                        question={question}
                        rows={rows}
                      />
                    ))}
                  </AnswerTable>
                </SectionCard>
              ))}
            </ExpansionPanel>
          </Box>
        ))
      ) : (
        <CardLine>
          {changesOnly
            ? "No answers have changed across applications."
            : "No form answers to show."}
        </CardLine>
      )}
    </Box>
  );
};
